import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { PublicLayout } from 'app/layouts/PublicLayout';
import { AppLayout } from 'app/layouts/AppLayout';
import { AdminLayout } from 'app/layouts/AdminLayout';
import { LessonLayout } from 'app/layouts/LessonLayout';
import { AuthGuard } from 'features/auth/components/AuthGuard';
import { GuestGuard } from 'features/auth/components/GuestGuard';
import { PermissionGuard } from 'shared/permissions/PermissionGuard';
import { SchoolActiveGuard } from 'features/schools/components/SchoolActiveGuard';
import { PageSkeleton } from 'components/ui/Skeleton';
import { ROUTES } from 'constants/routes';
import { PLATFORM_OPS_ROLES } from 'constants/roles';
import { AiRouteLanguageProvider } from 'features/ai/components/AiRouteLanguageProvider';

const HomePage = lazy(() => import('features/schools/pages/HomePage'));
const AboutPage = lazy(() => import('features/schools/pages/PublicPages').then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('features/schools/pages/PublicPages').then((m) => ({ default: m.ContactPage })));
const BlogPage = lazy(() => import('features/schools/pages/PublicPages').then((m) => ({ default: m.BlogPage })));
const PrivacyPage = lazy(() => import('features/schools/pages/PublicPages').then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('features/schools/pages/PublicPages').then((m) => ({ default: m.TermsPage })));
const PricingPage = lazy(() => import('features/schools/pages/PublicPages').then((m) => ({ default: m.PricingPage })));
const NotFoundPage = lazy(() => import('features/schools/pages/PublicPages').then((m) => ({ default: m.NotFoundPage })));
const SchoolDetailPage = lazy(() => import('features/schools/pages/SchoolDetailPage'));
const SchoolSyllabusPreviewPage = lazy(() => import('features/schools/pages/SchoolSyllabusPreviewPage'));
const ParashariLearnPage = lazy(() => import('schools/parashari/pages/ParashariLearnPage'));
const JaiminiLearnPage = lazy(() => import('schools/jaimini/pages/JaiminiLearnPage'));
const BnnLearnPage = lazy(() => import('schools/bnn/pages/BnnLearnPage'));
const KpLearnPage = lazy(() => import('schools/kp/pages/KpLearnPage'));
const LalKitabLearnPage = lazy(() => import('schools/lalKitab/pages/LalKitabLearnPage'));
const NumerologyLearnPage = lazy(() => import('schools/numerology/pages/NumerologyLearnPage'));
const SuryaSiddhantaLearnPage = lazy(() => import('schools/suryaSiddhanta/pages/SuryaSiddhantaLearnPage'));
const PanchangLearnPage = lazy(() => import('schools/panchang/pages/PanchangLearnPage'));
const VastuLearnPage = lazy(() => import('schools/vastu/pages/VastuLearnPage'));
const SwaraVigyanLearnPage = lazy(() => import('schools/swaraVigyan/pages/SwaraVigyanLearnPage'));
const YantraVidyaLearnPage = lazy(() => import('schools/yantraVidya/pages/YantraVidyaLearnPage'));
const TantraLearnPage = lazy(() => import('schools/tantra/pages/TantraLearnPage'));
const LoginPage = lazy(() => import('features/auth/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('features/auth/pages/ForgotPasswordPage'));
const AccessRequestPage = lazy(() => import('features/auth/pages/AccessRequestPage'));
const AcceptInvitePage = lazy(() => import('features/invites/pages/AcceptInvitePage'));
const DashboardPage = lazy(() => import('features/dashboard/pages/DashboardPage'));
const CourseDetailPage = lazy(() => import('features/courses/pages/CourseDetailPage'));
const LessonPage = lazy(() => import('features/lessons/pages/LessonPage'));
const BatchesPage = lazy(() => import('features/batches/pages/BatchesPage'));
const BatchDetailPage = lazy(() => import('features/batches/pages/BatchDetailPage'));
const AssignmentTakePage = lazy(() => import('features/assignments/pages/AssignmentTakePage'));
const AssignmentReviewPage = lazy(() => import('features/assignments/pages/AssignmentReviewPage'));
const BatchPathTopicPage = lazy(() => import('features/guru-path/pages/BatchPathTopicPage'));
const PathAssessmentTakePage = lazy(() => import('features/guru-path/pages/PathAssessmentTakePage'));
const CommunityPage = lazy(() => import('features/community/pages/CommunityPage'));
const AssessmentsPage = lazy(() => import('features/assessments/pages/AssessmentsPage'));
const InstitutionExamTakePage = lazy(
  () => import('features/assessments/pages/InstitutionExamTakePage')
);
const AiHubPage = lazy(() => import('features/ai/pages/AiPages'));
const AiChatPage = lazy(() => import('features/ai/pages/AiPages').then((m) => ({ default: m.AiChatPage })));
const AiTutorPage = lazy(() => import('features/ai/pages/AiPages').then((m) => ({ default: m.AiTutorPage })));
const AiChartLabPage = lazy(() => import('features/ai/pages/AiPages').then((m) => ({ default: m.AiChartLabPage })));
const PrivateNotesPage = lazy(() =>
  import('features/profile/pages/ProfilePages').then((m) => ({ default: m.PrivateNotesPage })),
);
const ProgressDashboardPage = lazy(() => import('schools/parashari/pages/ProgressDashboardPage'));
const SettingsDashboardPage = lazy(() => import('schools/parashari/pages/SettingsDashboardPage'));
const MyActivityPage = lazy(() => import('features/profile/pages/MyActivityPage'));
const SadhanaDashboardPage = lazy(() => import('schools/parashari/pages/SadhanaDashboardPage'));
const CertificatesPage = lazy(() => import('features/certificates/pages/CertificatesPage'));
const CertificateVerifyPage = lazy(
  () => import('features/certificates/pages/CertificateVerifyPage')
);
const PublicProgramsPage = lazy(() => import('features/programs/pages/PublicProgramsPage'));
const ProgramDetailPage = lazy(() => import('features/programs/pages/ProgramDetailPage'));
const AskGuruMarketingPage = lazy(() => import('features/marketing/pages/AskGuruMarketingPage'));
const CommunityMarketingPage = lazy(() => import('features/marketing/pages/CommunityMarketingPage'));
const CertificationsMarketingPage = lazy(() => import('features/marketing/pages/CertificationsMarketingPage'));
const BookmarksPage = lazy(() => import('features/profile/pages/ProfilePages').then((m) => ({ default: m.BookmarksPage })));
const NotesPage = lazy(() => import('features/profile/pages/ProfilePages').then((m) => ({ default: m.NotesPage })));
const StudyMaterialsPage = lazy(() => import('features/study-materials/pages/StudyMaterialsPage'));
const CalendarPage = lazy(() => import('features/calendar/pages/CalendarPage'));
const RemindersPage = lazy(() => import('features/profile/pages/RemindersPage'));
const AdminOverviewPage = lazy(() => import('features/admin/pages/AdminOverviewPage'));
const AdminAccessDeniedPage = lazy(
  () => import('features/admin/pages/AdminAccessDeniedPage')
);
const AdminUsersPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminUsersPage })));
const AdminTeachersPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminTeachersPage })));
const AdminSchoolsPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminSchoolsPage })));
const AdminBatchesPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminBatchesPage })));
const AdminCourseBuilderPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminCourseBuilderPage })));
const AdminPermissionsPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminPermissionsPage })));
const AdminAnalyticsPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminAnalyticsPage })));
const AdminPaymentsPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminPaymentsPage })));
const AdminNotificationsPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminNotificationsPage })));
const AdminModerationPage = lazy(() => import('features/admin/pages/AdminPages').then((m) => ({ default: m.AdminModerationPage })));
const SchoolsCatalogPage = lazy(() => import('features/schools/pages/SchoolsCatalogPage'));

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

const aiTitles: Record<string, string> = {
  tutor: 'AskGuru',
  chat: 'AI Chat',
  mentor: 'AI Mentor',
  revision: 'AI Revision',
  flashcards: 'AI Flashcards',
  'quiz-generator': 'AI Quiz Generator',
  'lesson-summary': 'AI Lesson Summary',
  'ask-anything': 'AI Ask Anything',
};

function AiToolPage({ tool }: { tool: string }) {
  return <AiChatPage title={aiTitles[tool] || 'AI Assistant'} />;
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: ROUTES.HOME, element: <SuspenseWrap><GuestGuard><HomePage /></GuestGuard></SuspenseWrap> },
      { path: ROUTES.ABOUT, element: <SuspenseWrap><AboutPage /></SuspenseWrap> },
      { path: ROUTES.CONTACT, element: <SuspenseWrap><ContactPage /></SuspenseWrap> },
      { path: ROUTES.BLOG, element: <SuspenseWrap><BlogPage /></SuspenseWrap> },
      { path: ROUTES.PRIVACY, element: <SuspenseWrap><PrivacyPage /></SuspenseWrap> },
      { path: ROUTES.TERMS, element: <SuspenseWrap><TermsPage /></SuspenseWrap> },
      { path: ROUTES.PRICING, element: <SuspenseWrap><PricingPage /></SuspenseWrap> },
      { path: ROUTES.MARKETING_ASK_GURU, element: <SuspenseWrap><AskGuruMarketingPage /></SuspenseWrap> },
      { path: '/ask-bhrigu', element: <Navigate to={ROUTES.MARKETING_ASK_GURU} replace /> },
      { path: ROUTES.MARKETING_SANGHA, element: <SuspenseWrap><CommunityMarketingPage /></SuspenseWrap> },
      { path: ROUTES.MARKETING_CERTIFICATIONS, element: <SuspenseWrap><CertificationsMarketingPage /></SuspenseWrap> },
      { path: '/schools/parashari', element: <Navigate to={ROUTES.PARASHARI_LEARN} replace /> },
      { path: '/schools/jaimini', element: <Navigate to={ROUTES.JAIMINI_LEARN} replace /> },
      { path: '/schools/bnn', element: <Navigate to={ROUTES.BNN_LEARN} replace /> },
      { path: '/schools/kp', element: <Navigate to={ROUTES.KP_LEARN} replace /> },
      { path: '/schools/lal-kitab', element: <Navigate to={ROUTES.LAL_KITAB_LEARN} replace /> },
      { path: ROUTES.SCHOOL_DETAIL, element: <SuspenseWrap><SchoolDetailPage /></SuspenseWrap> },
      { path: ROUTES.SCHOOL_SYLLABUS, element: <SuspenseWrap><SchoolSyllabusPreviewPage /></SuspenseWrap> },
      { path: ROUTES.LOGIN, element: <SuspenseWrap><LoginPage /></SuspenseWrap> },
      { path: ROUTES.REGISTER, element: <Navigate to={ROUTES.LOGIN} replace /> },
      { path: ROUTES.FORGOT_PASSWORD, element: <SuspenseWrap><ForgotPasswordPage /></SuspenseWrap> },
      { path: ROUTES.ACCESS_REQUEST, element: <SuspenseWrap><AccessRequestPage /></SuspenseWrap> },
      { path: ROUTES.INVITE, element: <SuspenseWrap><AcceptInvitePage /></SuspenseWrap> },
      { path: ROUTES.CERTIFICATE_VERIFY, element: <SuspenseWrap><CertificateVerifyPage /></SuspenseWrap> },
    ],
  },
  {
    element: <AppLayout />,
    children: [
      { path: ROUTES.SETTINGS, element: <SuspenseWrap><SettingsDashboardPage /></SuspenseWrap> },
      { path: ROUTES.MY_ACTIVITY, element: <SuspenseWrap><MyActivityPage /></SuspenseWrap> },
      { path: ROUTES.SCHOOLS, element: <SuspenseWrap><SchoolsCatalogPage /></SuspenseWrap> },
      { path: ROUTES.PROGRAMS, element: <SuspenseWrap><PublicProgramsPage /></SuspenseWrap> },
      { path: ROUTES.PROGRAM_DETAIL, element: <SuspenseWrap><ProgramDetailPage /></SuspenseWrap> },
      { path: '/schools/parashari', element: <Navigate to={ROUTES.PARASHARI_LEARN} replace /> },
      { path: '/schools/jaimini', element: <Navigate to={ROUTES.JAIMINI_LEARN} replace /> },
      { path: '/schools/bnn', element: <Navigate to={ROUTES.BNN_LEARN} replace /> },
      { path: '/schools/kp', element: <Navigate to={ROUTES.KP_LEARN} replace /> },
      { path: '/schools/lal-kitab', element: <Navigate to={ROUTES.LAL_KITAB_LEARN} replace /> },
      {
        element: (
          <AuthGuard>
            <Outlet />
          </AuthGuard>
        ),
        children: [
      { path: ROUTES.DASHBOARD, element: <SuspenseWrap><DashboardPage /></SuspenseWrap> },
      { path: ROUTES.COURSES, element: <Navigate to={ROUTES.SCHOOLS} replace /> },
      {
        path: '/courses/c0000000-0000-0000-0000-000000000001',
        element: <Navigate to={ROUTES.PARASHARI_LEARN} replace />,
      },
      { path: '/courses/course-1', element: <Navigate to={ROUTES.PARASHARI_LEARN} replace /> },
      { path: '/courses/course-3', element: <Navigate to={ROUTES.JAIMINI_LEARN} replace /> },
      {
        path: '/courses/c0000000-0000-0000-0000-000000000002',
        element: <Navigate to={ROUTES.JAIMINI_LEARN} replace />,
      },
      { path: ROUTES.COURSE_DETAIL, element: <SuspenseWrap><CourseDetailPage /></SuspenseWrap> },
      { path: ROUTES.BATCHES, element: <SuspenseWrap><BatchesPage /></SuspenseWrap> },
      { path: ROUTES.BATCH_DETAIL, element: <SuspenseWrap><BatchDetailPage /></SuspenseWrap> },
      {
        path: ROUTES.BATCH_ASSIGNMENT_TAKE,
        element: <SuspenseWrap><AssignmentTakePage /></SuspenseWrap>,
      },
      {
        path: ROUTES.BATCH_ASSIGNMENT_REVIEW,
        element: <SuspenseWrap><AssignmentReviewPage /></SuspenseWrap>,
      },
      { path: ROUTES.BATCH_PATH_TOPIC, element: <SuspenseWrap><BatchPathTopicPage /></SuspenseWrap> },
      {
        path: ROUTES.BATCH_PATH_ASSESSMENT_TAKE,
        element: <SuspenseWrap><PathAssessmentTakePage /></SuspenseWrap>,
      },
      { path: ROUTES.COMMUNITY, element: <SuspenseWrap><CommunityPage /></SuspenseWrap> },
      { path: ROUTES.COMMUNITY_CHANNEL, element: <SuspenseWrap><CommunityPage /></SuspenseWrap> },
      { path: ROUTES.ASSESSMENTS, element: <SuspenseWrap><AssessmentsPage /></SuspenseWrap> },
      {
        path: ROUTES.ASSESSMENT_TAKE,
        element: <SuspenseWrap><InstitutionExamTakePage /></SuspenseWrap>,
      },
      { path: ROUTES.CALENDAR, element: <SuspenseWrap><CalendarPage /></SuspenseWrap> },
      { path: ROUTES.REMINDERS, element: <SuspenseWrap><RemindersPage /></SuspenseWrap> },
      {
        path: ROUTES.PARASHARI_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="parashari">
              <ParashariLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      { path: ROUTES.JAIMINI_BRIDGE_COURSE_LEGACY, element: <Navigate to={ROUTES.JAIMINI_LEARN} replace /> },
      {
        path: `${ROUTES.JAIMINI_BRIDGE_COURSE_LEGACY}/:lessonSlug`,
        element: <Navigate to={ROUTES.JAIMINI_LEARN} replace />,
      },
      { path: ROUTES.JAIMINI_BRIDGE_COURSE, element: <Navigate to={ROUTES.JAIMINI_LEARN} replace /> },
      { path: ROUTES.JAIMINI_BRIDGE_SYLLABUS, element: <Navigate to={ROUTES.JAIMINI_LEARN} replace /> },
      {
        path: `${ROUTES.JAIMINI_LEARN}/courses/:courseSlug/syllabus`,
        element: <Navigate to={ROUTES.JAIMINI_LEARN} replace />,
      },
      {
        path: `${ROUTES.JAIMINI_LEARN}/courses/:courseSlug`,
        element: <Navigate to={ROUTES.JAIMINI_LEARN} replace />,
      },
      {
        path: ROUTES.JAIMINI_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="jaimini">
              <JaiminiLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.BNN_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="bnn">
              <BnnLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.KP_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="kp">
              <KpLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.LAL_KITAB_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="lal-kitab">
              <LalKitabLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.NUMEROLOGY_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="numerology">
              <NumerologyLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.SURYA_SIDDHANTA_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="surya-siddhanta">
              <SuryaSiddhantaLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.PANCHANG_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="panchang">
              <PanchangLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.VASTU_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="vastu">
              <VastuLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.SWARA_VIGYAN_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="swara-vigyan">
              <SwaraVigyanLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.YANTRA_VIDYA_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="yantra-vidya">
              <YantraVidyaLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      {
        path: ROUTES.TANTRA_LEARN,
        element: (
          <SuspenseWrap>
            <SchoolActiveGuard slug="tantra">
              <TantraLearnPage />
            </SchoolActiveGuard>
          </SuspenseWrap>
        ),
      },
      { path: '/ankotish-marga', element: <Navigate to={ROUTES.NUMEROLOGY_LEARN} replace /> },
      { path: ROUTES.SADHANA, element: <SuspenseWrap><SadhanaDashboardPage /></SuspenseWrap> },
      { path: ROUTES.PROFILE, element: <SuspenseWrap><ProgressDashboardPage /></SuspenseWrap> },
      { path: ROUTES.CERTIFICATES, element: <SuspenseWrap><CertificatesPage /></SuspenseWrap> },
      { path: ROUTES.BOOKMARKS, element: <SuspenseWrap><BookmarksPage /></SuspenseWrap> },
      { path: ROUTES.NOTES, element: <SuspenseWrap><NotesPage /></SuspenseWrap> },
      { path: ROUTES.STUDY_MATERIALS, element: <SuspenseWrap><StudyMaterialsPage /></SuspenseWrap> },
      { path: ROUTES.PRIVATE_NOTES, element: <SuspenseWrap><PrivateNotesPage /></SuspenseWrap> },
      { path: ROUTES.AI, element: <SuspenseWrap><AiRouteLanguageProvider><AiHubPage /></AiRouteLanguageProvider></SuspenseWrap> },
      { path: ROUTES.AI_CHART_LAB, element: <SuspenseWrap><AiRouteLanguageProvider><AiChartLabPage /></AiRouteLanguageProvider></SuspenseWrap> },
      { path: ROUTES.AI_TUTOR, element: <SuspenseWrap><AiRouteLanguageProvider><AiTutorPage /></AiRouteLanguageProvider></SuspenseWrap> },
      { path: ROUTES.AI_CHAT, element: <SuspenseWrap><AiRouteLanguageProvider><AiToolPage tool="chat" /></AiRouteLanguageProvider></SuspenseWrap> },
      { path: ROUTES.AI_MENTOR, element: <SuspenseWrap><AiRouteLanguageProvider><AiToolPage tool="mentor" /></AiRouteLanguageProvider></SuspenseWrap> },
      { path: ROUTES.AI_REVISION, element: <SuspenseWrap><AiRouteLanguageProvider><AiToolPage tool="revision" /></AiRouteLanguageProvider></SuspenseWrap> },
      { path: ROUTES.AI_FLASHCARDS, element: <SuspenseWrap><AiRouteLanguageProvider><AiToolPage tool="flashcards" /></AiRouteLanguageProvider></SuspenseWrap> },
      { path: ROUTES.AI_QUIZ, element: <SuspenseWrap><AiRouteLanguageProvider><AiToolPage tool="quiz-generator" /></AiRouteLanguageProvider></SuspenseWrap> },
      { path: ROUTES.AI_SUMMARY, element: <SuspenseWrap><AiRouteLanguageProvider><AiToolPage tool="lesson-summary" /></AiRouteLanguageProvider></SuspenseWrap> },
      { path: ROUTES.AI_ASK, element: <SuspenseWrap><AiRouteLanguageProvider><AiToolPage tool="ask-anything" /></AiRouteLanguageProvider></SuspenseWrap> },
        ],
      },
    ],
  },
  {
    element: (
      <AuthGuard>
        <LessonLayout />
      </AuthGuard>
    ),
    children: [
      { path: ROUTES.LESSON, element: <SuspenseWrap><LessonPage /></SuspenseWrap> },
    ],
  },
  {
    element: (
      <PermissionGuard
        roles={PLATFORM_OPS_ROLES}
        fallback={
          <AdminLayout>
            <SuspenseWrap>
              <AdminAccessDeniedPage />
            </SuspenseWrap>
          </AdminLayout>
        }
      >
        <AdminLayout />
      </PermissionGuard>
    ),
    children: [
      { path: ROUTES.ADMIN, element: <SuspenseWrap><AdminOverviewPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_USERS, element: <SuspenseWrap><AdminUsersPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_TEACHERS, element: <SuspenseWrap><AdminTeachersPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_SCHOOLS, element: <SuspenseWrap><AdminSchoolsPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_BATCHES, element: <SuspenseWrap><AdminBatchesPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_COURSE_BUILDER, element: <SuspenseWrap><AdminCourseBuilderPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_PERMISSIONS, element: <SuspenseWrap><AdminPermissionsPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_ANALYTICS, element: <SuspenseWrap><AdminAnalyticsPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_PAYMENTS, element: <SuspenseWrap><AdminPaymentsPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_NOTIFICATIONS, element: <SuspenseWrap><AdminNotificationsPage /></SuspenseWrap> },
      { path: ROUTES.ADMIN_MODERATION, element: <SuspenseWrap><AdminModerationPage /></SuspenseWrap> },
    ],
  },
  { path: '*', element: <SuspenseWrap><NotFoundPage /></SuspenseWrap> },
]);
